// src/pages/LinkShopPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchShops, useSearchProfiles, useAddShopMember } from '../../hooks/useShops';
import toast from 'react-hot-toast';
import { FaSearch, FaUserTag, FaStore } from 'react-icons/fa';

const LinkShopPage = () => {
    const [shopSearchTerm, setShopSearchTerm] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [selectedShop, setSelectedShop] = useState(null); // { id, name }
    const [selectedUser, setSelectedUser] = useState(null); // { id, username }

    const { data: shopsData, isFetching: isSearchingShops } = useSearchShops(shopSearchTerm);
    const { data: profilesData, isFetching: isSearchingProfiles } = useSearchProfiles(userSearchTerm);
    const { mutate: addMember, isPending: isAddingMember } = useAddShopMember();

    const handleLinkOwner = () => {
        if (!selectedShop || !selectedUser) {
            toast.error('Please select both a Shop and a User.');
            return;
        }

        // The admin's job is to set the initial OWNER
        addMember({
            shopId: selectedShop.id,
            userId: selectedUser.id,
            role: 'owner', // Default role for admin link
        }, {
            onSuccess: () => {
                // Clear selections on success
                setSelectedShop(null);
                setSelectedUser(null);
                setShopSearchTerm('');
                setUserSearchTerm('');
                toast.success(`Successfully set ${selectedUser.username} as OWNER of ${selectedShop.name}.`);
            },
            // onError is handled in the hook, but you can add specific logic here if needed
        });
    };

    const isLinkButtonDisabled = !selectedShop || !selectedUser || isAddingMember;

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <FaUserTag className="mr-3 text-indigo-600" /> Admin: Link Shop Owner
            </h2>
            <p className="text-gray-600 mb-8">Use this tool to establish the initial **owner** membership for a new shop.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Shop Search/Selection */}
                <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <FaStore className="mr-2 text-red-500" /> 1. Select Shop
                    </h3>
                    <input
                        type="text"
                        placeholder="Search Shop Name or Slug (e.g., techzone)"
                        value={shopSearchTerm}
                        onChange={(e) => {
                            setShopSearchTerm(e.target.value);
                            setSelectedShop(null); // Clear selection on new search
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                    />
                    {isSearchingShops && shopSearchTerm.length >= 2 && <p className="mt-2 text-sm text-gray-500">Searching...</p>}
                    
                    {shopsData && shopsData.length > 0 && (
                        <ul className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-gray-50">
                            {shopsData.map((shop) => (
                                <li
                                    key={shop.id}
                                    className={`p-3 cursor-pointer hover:bg-indigo-100 ${selectedShop?.id === shop.id ? 'bg-indigo-200 font-medium' : ''}`}
                                    onClick={() => {
                                        setSelectedShop(shop);
                                        setShopSearchTerm(shop.name);
                                    }}
                                >
                                    {shop.name} <span className="text-xs text-gray-500">({shop.slug})</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {selectedShop && (
                        <p className="mt-3 p-3 bg-green-100 text-green-800 rounded-lg">
                            **Selected Shop:** {selectedShop.name}
                        </p>
                    )}
                </div>

                {/* User Search/Selection */}
                <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <FaUserTag className="mr-2 text-blue-500" /> 2. Select User (Owner)
                    </h3>
                    <input
                        type="text"
                        placeholder="Search Username (e.g., annette)"
                        value={userSearchTerm}
                        onChange={(e) => {
                            setUserSearchTerm(e.target.value);
                            setSelectedUser(null); // Clear selection on new search
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                    />
                    {isSearchingProfiles && userSearchTerm.length >= 2 && <p className="mt-2 text-sm text-gray-500">Searching...</p>}

                    {profilesData && profilesData.length > 0 && (
                        <ul className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-gray-50">
                            {profilesData.map((profile) => (
                                <li
                                    key={profile.id}
                                    className={`p-3 cursor-pointer hover:bg-blue-100 ${selectedUser?.id === profile.id ? 'bg-blue-200 font-medium' : ''}`}
                                    onClick={() => {
                                        setSelectedUser(profile);
                                        setUserSearchTerm(profile.username);
                                    }}
                                >
                                    {profile.username}
                                </li>
                            ))}
                        </ul>
                    )}
                    {selectedUser && (
                        <p className="mt-3 p-3 bg-green-100 text-green-800 rounded-lg">
                            **Selected User:** {selectedUser.username}
                        </p>
                    )}
                </div>
            </div>

            {/* Action Button */}
            <div className="mt-10 pt-6 border-t border-gray-200">
                <button
                    onClick={handleLinkOwner}
                    disabled={isLinkButtonDisabled}
                    className={`w-full py-4 text-white font-bold rounded-lg transition duration-300 flex items-center justify-center ${
                        isLinkButtonDisabled
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-500'
                    }`}
                >
                    {isAddingMember ? (
                        <>
                            <FaStore className="animate-spin mr-3" /> Linking Owner...
                        </>
                    ) : (
                        <>
                            <FaUserTag className="mr-3" /> Set Owner for Selected Shop
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default LinkShopPage;